
/**
 * BASED ON: EASY_DUI_LandingPage_v02M_WIZARD
 * SOLAR_5080_LandingPage_v01 (React + TypeScript + React-Bootstrap)
 * Version: v01A (Zip-gate + modular sections)
 * Date: 2025-09-10 (America/Denver)
 *
 * Notes:
 * - Keeps the CTA "Get a Quote" → Zip Code mini-step (zip + OK).
 * - On OK, full form reveals with zip prefilled (as part of address).
 * - Form is intentionally simple and broken into modular sections so you can
 *   add/remove sections without touching others.
 */

import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Nav,
  Navbar,
  Carousel,
  Row,
  Spinner,
  InputGroup,
  ProgressBar,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap";

/* ============================================================
   CONFIG
============================================================ */
const VERSION_NUMBER = "v01A";
const SHOW_VERSION = true;
const TEST_MODE = true;

// Replace with your API endpoint if needed
const API_URL = "https://example.com/api/lead";

// Asset path for hero images if you have them
// const imagePath = "insurance/images/businessInsurance/"; //solar/images/Hero/";
const imagePath = "insurance/images/DUI-Insurance/";
const ImageInterval = 3000;

// Phone CTA (edit to your real number/hours)
const AGENT_PHONE_NUMBER = "(303) 337-4433";
const AGENT_PHONE_HOURS = "M–F 9am–5pm MT";

const AGENT_PHONE_BUTTON_OBJ = (
  <>
    <div className="d-none d-md-block text-body-secondary me-2">Speak with an advisor:</div>
    <OverlayTrigger placement="right" overlay={<Tooltip id="AgentHoursTip">{AGENT_PHONE_HOURS}</Tooltip>}>
      <Button variant="outline-primary" href="tel:3033375434">{AGENT_PHONE_NUMBER}</Button>
    </OverlayTrigger>
  </>
);

/* ============================================================
   HELPERS
============================================================ */
const US_STATES: { code: string; name: string }[] = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" },
];

const digitsOnly = (s: string) => (s || "").replace(/\D+/g, "");
const isValidEmail = (email: string) => /.+@.+\..+/.test(email);
const isValidPhone = (raw: string) => digitsOnly(raw).length === 10;
const isValidZipCode = (raw: string) => digitsOnly(raw).length === 5;
const formatPhonePretty = (raw: string) => {
  const d = digitsOnly(raw);
  return d.length === 10 ? `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}` : raw;
};

/* ============================================================
   TYPES (payload kept intentionally simple for this site)
============================================================ */
export interface SolarLeadPayload {
  fullName: string;
  email: string;
  phone: string;

  address1: string;
  address2?: string;
  city: string;
  state: string;
  zipCode: string;

  primaryReason: "lower_bill" | "backup_power" | "tax_credit" | "green" | "increase_home_value" | "other";
  maxMonthlyBill: string; // keep as string for backend flexibility (e.g., "$250", "250-300")

  wantsBattery: boolean;
  hasHotTub: boolean;
  hasPool: boolean;
  hasEV: boolean;

  notes?: string;

  utm?: { source?: string; medium?: string; campaign?: string };
  domainName: string;
  itemCategory: string;
  itemClass: string;
  itemType: string;
  clientVersion: string;
}

/* ============================================================
   EFFECTS (easy to swap)
============================================================ */
const FX_ZIP = "fx-reveal fade-slide-up";
const FX_FORM = "fx-reveal fade-scale-in";

/* ============================================================
   SECTION COMPONENTS (modular & swappable)
============================================================ */
type VP = {
  onBlur?: () => void;
  isValid?: boolean;
  isInvalid?: boolean;
  className?: string;
};

const SectionContact: React.FC<{
  fullName: string; setFullName: (v: string) => void;
  email: string; setEmail: (v: string) => void;
  phone: string; setPhone: (v: string) => void;
  vp: (name: string, pass: boolean) => VP; V: any;
}> = ({ fullName, setFullName, email, setEmail, phone, setPhone, vp, V }) => (
  <fieldset className="border rounded-3 p-3 bg-body-secondary">
    <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
      <strong>Contact</strong>
    </legend>
    <Row className="g-3 mt-1">
      <Col md={6}>
        <Form.Label htmlFor="fullName">Full Name</Form.Label>
        <Form.Control id="fullName" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Jane Doe" required {...vp("fullName", V.fullName)} />
        <Form.Control.Feedback type="invalid">Please enter your full name.</Form.Control.Feedback>
      </Col>

      <Col md={6}>
        <Form.Label htmlFor="email">Email</Form.Label>
        <Form.Control id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required {...vp("email", V.email)} />
        <Form.Control.Feedback type="invalid">Enter a valid email.</Form.Control.Feedback>
      </Col>

      <Col md={6}>
        <Form.Label htmlFor="phone">Phone</Form.Label>
        <Form.Control id="phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" required {...vp("phone", V.phone)} />
        <Form.Control.Feedback type="invalid">Enter a 10-digit phone.</Form.Control.Feedback>
      </Col>
    </Row>
  </fieldset>
);

const SectionAddress: React.FC<{
  address1: string; setAddress1: (v: string) => void;
  address2: string; setAddress2: (v: string) => void;
  city: string; setCity: (v: string) => void;
  state: string; setState: (v: string) => void;
  zipCode: string; setZipCode: (v: string) => void;
  vp: (name: string, pass: boolean) => VP; V: any;
}> = ({ address1, setAddress1, address2, setAddress2, city, setCity, state, setState, zipCode, setZipCode, vp, V }) => (
  <fieldset className="border rounded-3 p-3 bg-body-secondary">
    <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
      <strong>Home Address</strong>
    </legend>
    <Row className="g-3 mt-1">
      <Col md={8}>
        <Form.Label htmlFor="address1">Address</Form.Label>
        <Form.Control id="address1" value={address1} onChange={(e) => setAddress1(e.target.value)} placeholder="123 Main St" required {...vp("address1", V.address1)} />
        <Form.Control.Feedback type="invalid">Please enter your street address.</Form.Control.Feedback>
      </Col>
      <Col md={4}>
        <Form.Label htmlFor="address2">Unit (optional)</Form.Label>
        <Form.Control id="address2" value={address2} onChange={(e) => setAddress2(e.target.value)} placeholder="Apt / Unit" />
      </Col>
      <Col md={5}>
        <Form.Label htmlFor="city">City</Form.Label>
        <Form.Control id="city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="City" required {...vp("city", V.city)} />
        <Form.Control.Feedback type="invalid">Enter your city.</Form.Control.Feedback>
      </Col>
      <Col md={3}>
        <Form.Label htmlFor="state">State</Form.Label>
        <Form.Select id="state" value={state} onChange={(e) => setState(e.target.value)} required {...vp("state", V.state)}>
          <option value="">Select state</option>
          {US_STATES.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
        </Form.Select>
      </Col>
      <Col md={4}>
        <Form.Label htmlFor="zipCode">Zip Code</Form.Label>
        <Form.Control id="zipCode" inputMode="numeric" value={zipCode} onChange={(e) => setZipCode(digitsOnly(e.target.value).slice(0,5))} placeholder="#####" required {...vp("zip", V.zip)} />
        <Form.Control.Feedback type="invalid">Enter a 5-digit zip code.</Form.Control.Feedback>
      </Col>
    </Row>
  </fieldset>
);

const SectionEnergyProfile: React.FC<{
  primaryReason: SolarLeadPayload["primaryReason"]; setPrimaryReason: (v: SolarLeadPayload["primaryReason"]) => void;
  maxMonthlyBill: string; setMaxMonthlyBill: (v: string) => void;
  wantsBattery: boolean; setWantsBattery: (v: boolean) => void;
  hasHotTub: boolean; setHasHotTub: (v: boolean) => void;
  hasPool: boolean; setHasPool: (v: boolean) => void;
  hasEV: boolean; setHasEV: (v: boolean) => void;
  vp: (name: string, pass: boolean) => VP; V: any;
}> = (props) => {
  const {
    primaryReason, setPrimaryReason, maxMonthlyBill, setMaxMonthlyBill,
    wantsBattery, setWantsBattery, hasHotTub, setHasHotTub, hasPool, setHasPool, hasEV, setHasEV, vp, V
  } = props;

  return (
    <fieldset className="border rounded-3 p-3 bg-body-secondary">
      <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
        <strong>Solar Goals & Power Use</strong>
      </legend>

      <Row className="g-3 mt-1">
        <Col md={6}>
          <Form.Label htmlFor="primaryReason">Primary reason you’re exploring solar</Form.Label>
          <Form.Select id="primaryReason" value={primaryReason} onChange={(e) => setPrimaryReason(e.target.value as SolarLeadPayload["primaryReason"])} required {...vp("primaryReason", V.primaryReason)}>
            <option value="">Select reason</option>
            <option value="lower_bill">Lower my electric bill</option>
            <option value="backup_power">Backup power / outages</option>
            <option value="tax_credit">Tax credit / incentives</option>
            <option value="green">Go green / sustainability</option>
            <option value="increase_home_value">Increase home value</option>
            <option value="other">Other</option>
          </Form.Select>
        </Col>

        <Col md={6}>
          <Form.Label htmlFor="maxMonthlyBill">Most expensive monthly electric bill (approx.)</Form.Label>
          <Form.Control id="maxMonthlyBill" value={maxMonthlyBill} onChange={(e) => setMaxMonthlyBill(e.target.value)} placeholder="$250" required {...vp("maxMonthlyBill", V.maxMonthlyBill)} />
          <Form.Control.Feedback type="invalid">Please provide an amount (e.g., $250 or 250–300).</Form.Control.Feedback>
        </Col>

        <Col xs={12}>
          <Row className="g-3">
            <Col md={3}>
              <Form.Check
                type="switch"
                id="wantsBattery"
                label="Interested in backup battery"
                checked={wantsBattery}
                onChange={(e) => setWantsBattery(e.currentTarget.checked)}
              />
            </Col>
            <Col md={3}>
              <Form.Check
                type="switch"
                id="hasHotTub"
                label="Hot tub"
                checked={hasHotTub}
                onChange={(e) => setHasHotTub(e.currentTarget.checked)}
              />
            </Col>
            <Col md={3}>
              <Form.Check
                type="switch"
                id="hasPool"
                label="Pool"
                checked={hasPool}
                onChange={(e) => setHasPool(e.currentTarget.checked)}
              />
            </Col>
            <Col md={3}>
              <Form.Check
                type="switch"
                id="hasEV"
                label="Electric vehicle(s)"
                checked={hasEV}
                onChange={(e) => setHasEV(e.currentTarget.checked)}
              />
            </Col>
          </Row>
        </Col>
      </Row>
    </fieldset>
  );
};

const SectionNotes: React.FC<{ notes: string; setNotes: (v: string) => void; vp: (name: string, pass: boolean) => VP; }> = ({ notes, setNotes, vp }) => (
  <fieldset className="border rounded-3 p-3 bg-body-secondary">
    <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
      <strong>Notes / Comments</strong>
    </legend>
    <Form.Control
      as="textarea"
      rows={3}
      id="notes"
      value={notes}
      onChange={(e) => setNotes(e.target.value)}
      placeholder="Anything else we should know? Roof age, HOA, timeline, etc."
      {...vp("notes", notes.trim().length >= 0)}
    />
  </fieldset>
);

/* ============================================================
   MAIN
============================================================ */
export const SOLAR_5080_LandingPage_v01: React.FC = () => {
  const formRef = useRef<HTMLDivElement | null>(null);
  const zipRef = useRef<HTMLDivElement | null>(null);

  // CTA flow
  const [showZipGate, setShowZipGate] = useState(false);
  const [showForm, setShowForm] = useState(false);

  // Zip gate state
  const [zipInput, setZipInput] = useState("");
  const [zipError, setZipError] = useState<string | null>(null);

  // Mode + wizard
  const [mode, setMode] = useState<"wizard" | "vertical">("wizard");
  const isWizard = mode === "wizard";
  const [currentStep, setCurrentStep] = useState(0);
  const steps = ["1. Contact", "2. Address", "3. Solar Goals", "4. Review"] as const;

  // Touched + validators
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const isTouched = (name: string) => !!touched[name];
  const markTouched = (name: string) => setTouched((t) => ({ ...t, [name]: true }));

  const vp = (name: string, pass: boolean): VP => ({
    onBlur: () => markTouched(name),
    isValid: isTouched(name) && pass,
    isInvalid: isTouched(name) && !pass,
    className: isTouched(name) && pass ? "bg-success-subtle" : undefined,
  });

  /* ------------------- Responsive scroll helpers ------------------- */
  const isPhoneViewport = () =>
    (typeof window !== "undefined") &&
    (window.matchMedia?.("(max-width: 767.98px)")?.matches || window.innerWidth < 768);

  const smartScrollTo = (el: HTMLElement | null) => {
    if (typeof window === "undefined") return;
    if (isPhoneViewport()) {
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
  };

  /* ------------------- Basics ------------------- */
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  /* ------------------- Address ------------------- */
  const [address1, setAddress1] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");

  /* ------------------- Energy Profile ------------------- */
  const [primaryReason, setPrimaryReason] = useState<SolarLeadPayload["primaryReason"]>("" as any);
  const [maxMonthlyBill, setMaxMonthlyBill] = useState("");
  const [wantsBattery, setWantsBattery] = useState(false);
  const [hasHotTub, setHasHotTub] = useState(false);
  const [hasPool, setHasPool] = useState(false);
  const [hasEV, setHasEV] = useState(false);

  /* ------------------- Notes ------------------- */
  const [notes, setNotes] = useState("");

  /* ------------------- Submit / server ------------------- */
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitOk, setSubmitOk] = useState(false);
  const [serverStatus, setServerStatus] =
    useState<null | { ok: boolean; status?: number; id?: string; message?: string }>(null);

  /* ------------------- Validators ------------------- */
  const V = {
    fullName: fullName.trim().length >= 2,
    email: isValidEmail(email),
    phone: isValidPhone(phone),

    address1: address1.trim().length >= 3,
    city: city.trim().length >= 2,
    state: state !== "",
    zip: isValidZipCode(zipCode),

    primaryReason: !!primaryReason,
    maxMonthlyBill: maxMonthlyBill.trim().length >= 1,
  };

  const valid = useMemo(() => {
    return V.fullName && V.email && V.phone &&
           V.address1 && V.city && V.state && V.zip &&
           V.primaryReason && V.maxMonthlyBill;
  }, [V]);

  function isStepValid(stepIdx: number): boolean {
    switch (stepIdx) {
      case 0: return V.fullName && V.email && V.phone;
      case 1: return V.address1 && V.city && V.state && V.zip;
      case 2: return V.primaryReason && V.maxMonthlyBill;
      case 3: return submitOk === true;
      default: return false;
    }
  }

  /* ------------------- CTA handlers: Zip gate + Form ------------------- */
  const revealZipGate = () => {
    setShowForm(false);
    setShowZipGate(true);
    setTimeout(() => smartScrollTo(zipRef.current!), 50);
  };

  const handleZipOk = () => {
    const cleaned = digitsOnly(zipInput).slice(0, 5);
    if (cleaned.length !== 5) {
      setZipError("Please enter a valid 5-digit zip code.");
      return;
    }
    setZipError(null);
    setZipCode(cleaned);
    setShowZipGate(false);
    setShowForm(true);
    setTouched((t) => ({ ...t, zip: true })); // mark zip as touched/valid
    setTimeout(() => smartScrollTo(formRef.current!), 50);
  };

  /* ------------------- Submit ------------------- */
  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();

    if (isWizard && currentStep < 2) {
      // advance the wizard if not on final data step
      if (isStepValid(currentStep)) setCurrentStep((s) => s + 1);
      return;
    }

    setSubmitError(null);
    setSubmitOk(false);
    setServerStatus(null);

    // touch fields so validation shows
    setTouched((t) => ({
      ...t,
      fullName: true, email: true, phone: true,
      address1: true, city: true, state: true, zip: true,
      primaryReason: true, maxMonthlyBill: true
    }));

    if (!valid) {
      setSubmitError("Please complete all required fields.");
      return;
    }

    const payload: SolarLeadPayload = {
      fullName: fullName.trim(),
      email: email.trim(),
      phone: digitsOnly(phone),

      address1: address1.trim(),
      address2: address2.trim() || undefined,
      city: city.trim(),
      state: state,
      zipCode: zipCode,

      primaryReason,
      maxMonthlyBill: maxMonthlyBill.trim(),

      wantsBattery,
      hasHotTub,
      hasPool,
      hasEV,

      notes: notes?.trim() || undefined,

      utm: {
        source: new URLSearchParams(window.location.search).get("utm_source") || undefined,
        medium: new URLSearchParams(window.location.search).get("utm_medium") || undefined,
        campaign: new URLSearchParams(window.location.search).get("utm_campaign") || undefined,
      },
      domainName: String(window.location.hostname || "5080.solar"),
      itemCategory: "Customer.solar.lead",
      itemClass: "lead.request",
      itemType: "solar.5280.solar",
      clientVersion: String(VERSION_NUMBER),
    };

    try {
      setSubmitting(true);

      if (TEST_MODE) {
        console.log("[TEST_MODE] Submitting payload:", payload);
        await new Promise((r) => setTimeout(r, 700));
        setServerStatus({ ok: true, status: 200, id: "TEST-" + Math.random().toString(36).slice(2, 8) });
        setSubmitOk(true);
        if (isWizard) setCurrentStep(3);
        return;
      }

      const res = await fetch(API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        mode: "cors",
      });

      let resJson: any = null;
      try { resJson = await res.json(); } catch {}

      if (!res.ok) {
        const msg = (resJson && (resJson.error || resJson.message)) || `Request failed: ${res.status}`;
        setServerStatus({ ok: false, status: res.status, message: msg });
        throw new Error(msg);
      }

      setServerStatus({ ok: true, status: res.status, id: resJson?.id || resJson?.requestId });
      setSubmitOk(true);
      if (isWizard) setCurrentStep(3);
    } catch (err: any) {
      setSubmitError(err?.message || String(err));
      setSubmitOk(false);
    } finally {
      setSubmitting(false);
    }
  };

  const ConfirmationBlock: React.FC = () => (
    <fieldset className="border rounded-3 p-3 bg-body-secondary">
      <legend className="px-2 fs-6 text-muted" style={{ top: "0.5rem", background: "var(--bs-secondary-bg)" }}>
        <strong>Confirmation</strong>
      </legend>

      {submitOk ? (
        <>
          <Alert variant="success" className="mb-3">
            <div className="fw-semibold mb-1">Thanks! We’ll contact you shortly.</div>
            <div className="small mb-0">
              {serverStatus?.ok
                ? <>Server status: <strong>OK</strong>{serverStatus?.status ? ` • ${serverStatus.status}` : ""}{serverStatus?.id ? ` • ID: ${serverStatus.id}` : ""}</>
                : "Server status: Unknown"}
            </div>
          </Alert>

          <Row className="g-3 small">
            <Col md={6}>
              <Card className="h-100">
                <Card.Header className="fw-semibold">Contact</Card.Header>
                <Card.Body>
                  <div><strong>Name:</strong> {fullName}</div>
                  <div><strong>Email:</strong> {email}</div>
                  <div><strong>Phone:</strong> {formatPhonePretty(phone)}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100">
                <Card.Header className="fw-semibold">Address</Card.Header>
                <Card.Body>
                  <div>{address1}{address2 ? `, ${address2}` : ""}</div>
                  <div>{city}, {state} {zipCode}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={12}>
              <Card>
                <Card.Header className="fw-semibold">Solar Goals</Card.Header>
                <Card.Body>
                  <Row xs={1} md={3}>
                    {/* <Col><strong>Reason:</strong> {String(primaryReason || "—").replaceAll("_", " ")}</Col> */}
                    <Col><strong>Reason:</strong>     {String(primaryReason || "—").replace(/_/g, " ")}</Col> 
                    <Col><strong>Peak Bill:</strong>  {maxMonthlyBill || "—"}</Col>
                    <Col><strong>Battery:</strong>    {wantsBattery ? "Yes" : "No"}</Col>
                  </Row>
                  <Row xs={1} md={3} className="mt-2">
                    <Col><strong>Hot tub:</strong> {hasHotTub ? "Yes" : "No"}</Col>
                    <Col><strong>Pool:</strong> {hasPool ? "Yes" : "No"}</Col>
                    <Col><strong>EV(s):</strong> {hasEV ? "Yes" : "No"}</Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
            {notes.trim() && (
              <Col md={12}>
                <Card>
                  <Card.Header className="fw-semibold">Notes</Card.Header>
                  <Card.Body>{notes}</Card.Body>
                </Card>
              </Col>
            )}
          </Row>

          <div className="d-flex justify-content-end gap-2 mt-3">
            <Button
              variant="secondary"
              onClick={() => {
                // reset flow
                setFullName(""); setEmail(""); setPhone("");
                setAddress1(""); setAddress2(""); setCity(""); setState(""); setZipCode("");
                setPrimaryReason("" as any); setMaxMonthlyBill("");
                setWantsBattery(false); setHasHotTub(false); setHasPool(false); setHasEV(false);
                setNotes("");
                setTouched({});
                setSubmitOk(false);
                setServerStatus(null);
                setCurrentStep(0);
                setShowForm(false);
                setShowZipGate(false);
                setZipInput("");
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              Start New Request
            </Button>
          </div>
        </>
      ) : (
        <Alert variant="warning">We couldn’t confirm the server response. Please go back and try submitting again.</Alert>
      )}
    </fieldset>
  );

  /* ------------------- HERO Slide (static image slots; replace paths if desired) ------------------- */
  // const slides = [
  //   { src: imagePath + "OfficeBuilding01.jpg", title: "Power your home with 5080.SOLAR", sub: "Lower bills. Clean energy. Backup options." },
  //   { src: imagePath + "BizInt-AdobeStock_1188218822_Preview-natural.jpeg", title: "Simple. Fast. Local.", sub: "Tell us a little about your home—we’ll do the rest." }
  // ];
  interface Slide { src: string; alt: string; title: string; subline: string; smallCaption: string; }
  
const slides: Slide[] = [
  {
    src: imagePath + "AdobeStock_1463253262.jpg",
    alt: "Switch to clean solar energy and reduce your electric bill.",
    title: "Lower Your Energy Costs",
    subline: "Generate your own power and keep more money in your pocket.",
    smallCaption: "Save thousands over the lifetime of your system.",
  },
  {
    src: imagePath + "AdobeStock_293465118.jpg",
    alt: "Get a free solar quote in minutes.",
    title: "Fast & Free Quote",
    subline: "Submit once—we’ll design a system tailored to your home.",
    smallCaption: "No obligation. Simple and transparent.",
  },
  {
    src: imagePath + "AdobeStock_1360258027.jpg",
    alt: "Home with solar panels generating reliable power.",
    title: "Energy Independence",
    subline: "Take control of your power with a rooftop solar system.",
    smallCaption: "Protect yourself from rising utility rates.",
  },
  {
    src: imagePath + "AdobeStock_410612951.jpg",
    alt: "Affordable solar installation programs available.",
    title: "Affordable Solar Options",
    subline: "Flexible financing and incentive programs make solar accessible.",
    smallCaption: "Go solar with little or no upfront cost.",
  },
  {
    src: imagePath + "AdobeStock_110420565.jpg",
    alt: "Backup battery ensures power during blackouts.",
    title: "Backup Power Security",
    subline: "Add a battery so your lights stay on—even in a blackout.",
    smallCaption: "Peace of mind when you need it most.",
  },
  {
    src: imagePath + "AdobeStock_318087469A.jpg",
    alt: "Talk to licensed solar energy advisors.",
    title: "Talk to Solar Experts",
    subline: "Our advisors will guide you through incentives, savings, and design.",
    smallCaption: "Trusted guidance. Real answers.",
  },
];


  const [activeSlide, setActiveSlide] = useState(0);
  const active = slides[activeSlide] || slides[0];

  return (
    <div className="bg-body">
      {/* Swappable effect CSS (drop-in). Swap class names FX_ZIP / FX_FORM above. */}
      <style>{`
        .fx-reveal { --fx-dur: .45s; animation-duration: var(--fx-dur); animation-fill-mode: both; }
        .fade-slide-up { animation-name: fadeSlideUp; }
        .fade-scale-in { animation-name: fadeScaleIn; }
        @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(16px) } to { opacity: 1; transform: translateY(0) } }
        @keyframes fadeScaleIn { from { opacity: 0; transform: scale(.98) } to { opacity: 1; transform: scale(1) } }
      `}</style>

      {/* Topbar */}
      <Navbar expand="md" bg="light" className="border-bottom sticky-top">
        <Container>
          <Navbar.Brand href="#" className="fw-bold">
            5080<span className="text-primary">.SOLAR</span>
            {SHOW_VERSION && <Badge bg="secondary" className="ms-2">{VERSION_NUMBER}</Badge>}
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="topbar" />
          <Navbar.Collapse id="topbar" className="justify-content-end">
            <Nav className="align-items-center gap-2">
              {AGENT_PHONE_BUTTON_OBJ}
              <Button onClick={revealZipGate}>Get a Quote</Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO */}
      <section id="home" className="position-relative overflow-hidden" style={{ minHeight: "640px" }}>
        {/* <div className="position-absolute top-0 start-0 end-0 bottom-0" style={{ zIndex: 0 }} aria-hidden> */}
          {/* Use any carousel library you prefer; leaving a simple CSS background swap for brevity */}


    <div className="position-absolute top-0 start-0 end-0 bottom-0" style={{ zIndex: 0 }} aria-hidden>
          <Carousel
            activeIndex={activeSlide}
            onSelect={(i) => setActiveSlide(i)}
            controls
            indicators
            fade
            interval={ImageInterval}
            className="w-100 h-100"
          >

            {/* style={{border: "2px dashed green"}} */}
            {slides.map((s, idx) => (
              <Carousel.Item key={idx} className="w-100 h-100" >
                <div
                  role="img"
                  aria-label={s.alt}
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 760,
                    backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.00)), url('${s.src}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    // border: '2px dashed orange'
                  }}
                />
                <Carousel.Caption className="text-start" style={{ bottom: "40%" }} />
              </Carousel.Item>
            ))}
          </Carousel>
        </div>




{/*           
          <div
            role="img"
            aria-label={active.title}
            style={{
              width: "100%",
              height: "100%",
              minHeight: 640,
              backgroundImage: `linear-gradient(to right, rgba(0,0,0,0.55), rgba(0,0,0,0.25) 40%, rgba(0,0,0,0.10) 60%, rgba(0,0,0,0.00)), url('${active.src}')`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          />
        </div> */}

        {/* Foreground content */}
        <Container className="position-relative" style={{ zIndex: 1 }}>
          <Row className="align-items-start g-0 g-sm-4 p-0 p-sm-4 p-lg-5">
            <Col lg={6} className="text-white">
              <div className="pe-lg-4">
                <h1 className="display-5 fw-bold" style={{ marginTop: "320px" }}>{active.title}</h1>
                <p className="lead mb-3">{active.subline}</p>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <Badge bg="light" text="dark">Local Expertise</Badge>
                  <Badge bg="light" text="dark">Battery Options</Badge>
                  <Badge bg="light" text="dark">Incentive Guidance</Badge>
                </div>
                <div className="mt-4 d-flex gap-2">
                  <Button variant="outline-light" onClick={revealZipGate}>Get a Quote</Button>
                </div>
              </div>
            </Col>

            {/* Right column: Zip Gate → Full Form */}
            <Col lg={6}>
              {/* ZIP GATE */}
              {showZipGate && (
                <div ref={zipRef} className={FX_ZIP}>
                  <Card className="shadow-lg" style={{ backdropFilter: "blur(6px)" }}>
                    <Card.Header className="bg-white">
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="fw-semibold">Start Your Quote</div>
                        <Badge bg="success">Secure</Badge>
                      </div>
                    </Card.Header>
                    <Card.Body className="p-4">
                      {zipError && (
                        <Alert variant="danger" onClose={() => setZipError(null)} dismissible>
                          {zipError}
                        </Alert>
                      )}
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleZipOk();
                        }}
                      >
                        <Form.Label htmlFor="zipGate">Enter Your Zip Code</Form.Label>
                        <InputGroup className="mb-2">
                          <Form.Control
                            id="zipGate"
                            inputMode="numeric"
                            maxLength={5}
                            placeholder="#####"
                            value={zipInput}
                            onChange={(e) => setZipInput(digitsOnly(e.target.value).slice(0, 5))}
                            autoFocus
                          />
                          <Button
                            type="submit"
                            variant="primary"
                            disabled={digitsOnly(zipInput).length !== 5}
                          >
                            OK
                          </Button>
                        </InputGroup>
                        <div className="form-text">We’ll prefill your address zip.</div>
                      </Form>
                    </Card.Body>
                  </Card>
                </div>
              )}

              {/* FULL FORM */}
              {showForm && (
                <div className={FX_FORM}>
                  <Card className="shadow-lg" style={{ backdropFilter: "blur(6px)" }}>
                    <Card.Header className="bg-white">
                      <div className="d-flex align-items-center justify-content-between">
                        <div>
                          <div className="fw-semibold">Fast, No-Obligation Quote</div>
                          <small className="text-body-secondary">We’ll match you with the right solar options.</small>
                        </div>
                        <Badge bg="success">Secure</Badge>
                      </div>
                    </Card.Header>
                    <Card.Body ref={formRef}>
                      {/* MODE TOGGLE + (Wizard) Stepper */}
                      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                        <div className="d-flex align-items-center gap-2">
                          <Form.Check
                            type="switch"
                            id="modeSwitch"
                            label={isWizard ? "Wizard mode" : "Vertical mode"}
                            checked={isWizard}
                            onChange={(e) => {
                              setMode(e.currentTarget.checked ? "wizard" : "vertical");
                              setTouched({});
                              if (submitOk && e.currentTarget.checked) setCurrentStep(3);
                              else setCurrentStep(0);
                            }}
                          />
                          <small className="text-muted">Switch to see all fields in one view.</small>
                        </div>

                        <div style={{ minWidth: 280 }}>
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <Badge bg="secondary">Step {isWizard ? currentStep + 1 : (submitOk ? 2 : 1)} of {isWizard ? 4 : (submitOk ? 2 : 1)}</Badge>
                            <small className="text-muted">{isWizard ? steps[currentStep] : (submitOk ? "Confirmation" : "All fields")}</small>
                          </div>
                          <ProgressBar now={isWizard ? ((currentStep + 1) / 4) * 100 : (submitOk ? 100 : 50)} />
                        </div>
                      </div>

                      {/* Validation messages */}
                      {/* (kept simple—only show on submit or when touched) */}

                      <Form onSubmit={handleSubmit} noValidate>
                        {/* WIZARD VIEW */}
                        {isWizard ? (
                          <>
                            {currentStep === 0 && (
                              <SectionContact
                                fullName={fullName} setFullName={setFullName}
                                email={email} setEmail={setEmail}
                                phone={phone} setPhone={setPhone}
                                vp={vp} V={V}
                              />
                            )}

                            {currentStep === 1 && (
                              <SectionAddress
                                address1={address1} setAddress1={setAddress1}
                                address2={address2} setAddress2={setAddress2}
                                city={city} setCity={setCity}
                                state={state} setState={setState}
                                zipCode={zipCode} setZipCode={setZipCode}
                                vp={vp} V={V}
                              />
                            )}

                            {currentStep === 2 && (
                              <>
                                <SectionEnergyProfile
                                  primaryReason={primaryReason} setPrimaryReason={setPrimaryReason}
                                  maxMonthlyBill={maxMonthlyBill} setMaxMonthlyBill={setMaxMonthlyBill}
                                  wantsBattery={wantsBattery} setWantsBattery={setWantsBattery}
                                  hasHotTub={hasHotTub} setHasHotTub={setHasHotTub}
                                  hasPool={hasPool} setHasPool={setHasPool}
                                  hasEV={hasEV} setHasEV={setHasEV}
                                  vp={vp} V={V}
                                />
                                <div className="mt-3">
                                  <SectionNotes notes={notes} setNotes={setNotes} vp={vp} />
                                </div>
                              </>
                            )}

                            {currentStep === 3 && <ConfirmationBlock />}

                            {/* Wizard controls */}
                            {currentStep < 3 && (
                              <div className="d-flex justify-content-between mt-3">
                                <Button
                                  type="button"
                                  variant="outline-secondary"
                                  onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
                                  disabled={currentStep === 0}
                                >
                                  Previous
                                </Button>
                                {currentStep < 2 ? (
                                  <Button
                                    type="button"
                                    onClick={() => {
                                      // Mark relevant fields as touched for the current step
                                      if (currentStep === 0) {
                                        setTouched((t) => ({ ...t, fullName: true, email: true, phone: true }));
                                      } else if (currentStep === 1) {
                                        setTouched((t) => ({ ...t, address1: true, city: true, state: true, zip: true }));
                                      }
                                      if (isStepValid(currentStep)) setCurrentStep((s) => s + 1);
                                    }}
                                    disabled={!isStepValid(currentStep)}
                                  >
                                    Next
                                  </Button>
                                ) : (
                                  <Button type="submit" variant="primary" size="lg" disabled={submitting || !valid}>
                                    {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                                  </Button>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          /* VERTICAL VIEW */
                          <>
                            {!submitOk ? (
                              <>
                                <SectionContact
                                  fullName={fullName} setFullName={setFullName}
                                  email={email} setEmail={setEmail}
                                  phone={phone} setPhone={setPhone}
                                  vp={vp} V={V}
                                />
                                <div className="mt-3">
                                  <SectionAddress
                                    address1={address1} setAddress1={setAddress1}
                                    address2={address2} setAddress2={setAddress2}
                                    city={city} setCity={setCity}
                                    state={state} setState={setState}
                                    zipCode={zipCode} setZipCode={setZipCode}
                                    vp={vp} V={V}
                                  />
                                </div>
                                <div className="mt-3">
                                  <SectionEnergyProfile
                                    primaryReason={primaryReason} setPrimaryReason={setPrimaryReason}
                                    maxMonthlyBill={maxMonthlyBill} setMaxMonthlyBill={setMaxMonthlyBill}
                                    wantsBattery={wantsBattery} setWantsBattery={setWantsBattery}
                                    hasHotTub={hasHotTub} setHasHotTub={setHasHotTub}
                                    hasPool={hasPool} setHasPool={setHasPool}
                                    hasEV={hasEV} setHasEV={setHasEV}
                                    vp={vp} V={V}
                                  />
                                </div>
                                <div className="mt-3">
                                  <SectionNotes notes={notes} setNotes={setNotes} vp={vp} />
                                </div>

                                {/* Submit */}
                                <div className="d-grid mt-3">
                                  <Button type="submit" variant="primary" size="lg" disabled={!valid || submitting}>
                                    {submitting ? (<><Spinner animation="border" size="sm" className="me-2" /> Submitting...</>) : (<>Get My Quote</>)}
                                  </Button>
                                  <div className="text-center mt-2 text-body-secondary" style={{ fontSize: "0.9rem" }}>
                                    By clicking, you agree we may contact you about solar options. No spam. You can opt out anytime.
                                  </div>
                                </div>
                              </>
                            ) : (
                              <ConfirmationBlock />
                            )}
                          </>
                        )}
                      </Form>
                    </Card.Body>
                  </Card>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      {/* Footer */}
      <footer className="py-4 border-top">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="small text-body-secondary">
              © {new Date().getFullYear()} 5080.SOLAR. All rights reserved.
              <div className="mt-2">
                Information on this page is general and not financial advice. {VERSION_NUMBER}
              </div>
            </Col>
            <Col md={6} className="d-flex justify-content-md-end mt-3 mt-md-0 gap-2">
              {AGENT_PHONE_BUTTON_OBJ}
              <Button onClick={revealZipGate}>Get a Quote</Button>
            </Col>
          </Row>
        </Container>
      </footer>
    </div>
  );
};

export default SOLAR_5080_LandingPage_v01;
